/**
 * Écran « Suivi de l'élève & IA » (API v2) : onglets Pédagogie, IA, Traces.
 *
 * - Filtres (périmètre, période, onglet) dans l'URL : lien partageable (m-16).
 * - Onglets NON détruits : chaque onglet garde ses propres filtres (service,
 *   filtres de traces, pagination) quand on en change (F-FE-03). Un onglet
 *   inactif ne charge rien ; redevenu actif, il ne recharge que si les
 *   filtres ou le jeton d'actualisation ont changé.
 * - « Actualiser » incrémente un `refreshToken` écouté par TOUS les onglets,
 *   traces comprises.
 * - 403 de rôle → écran « rôle requis » ; 403 de périmètre → message dans
 *   l'onglet, la barre de filtres reste utilisable.
 */
import React, { useState } from 'react';
import { Alert, Space, Tabs } from 'antd';
import { CodeOutlined, FileSearchOutlined, RobotOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../components/layout/AppLayout';
import MonitoringFilterBar from './shared/MonitoringFilterBar';
import { filtersReady, useMonitoringFilters } from './shared/useMonitoringFilters';
import { useScopePreselect, useScopes } from './shared/useScopes';
import PedagogyTab from './tabs/PedagogyTab';
import AiTab from './tabs/AiTab';
import TracesTab from './tabs/TracesTab';

const TABS = ['pedagogy', 'ai', 'traces'] as const;
type TabKey = (typeof TABS)[number];

const MonitoringStudents: React.FC = () => {
    const { t } = useTranslation();
    const [refreshToken, setRefreshToken] = useState(0);
    // `/scopes` d'abord : il fournit `today`, d'où part la période par défaut.
    const scopes = useScopes(refreshToken);
    const { filters, preset, tab, setPeriod, setScope, setTab } = useMonitoringFilters<TabKey>(
        TABS,
        'pedagogy',
        scopes.data?.today ?? null
    );
    useScopePreselect(scopes.data, filters, setScope);

    const ready = scopes.status === 'success' && filtersReady(filters);

    const title = t('monitoring.students.title');

    if (scopes.status === 'role_required') {
        return (
            <AppLayout title={title}>
                <Alert type="error" showIcon data-testid="role-required" message={t('monitoring.errors.roleRequired')} />
            </AppLayout>
        );
    }

    const tabProps = { filters, ready, refreshToken };

    return (
        <AppLayout title={title}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <MonitoringFilterBar
                    scopes={scopes.data}
                    filters={filters}
                    preset={preset}
                    onScopeChange={setScope}
                    onPeriodChange={setPeriod}
                    onRefresh={() => setRefreshToken((n) => n + 1)}
                />
                {scopes.status === 'error' && (
                    <Alert type="error" showIcon message={t('monitoring.errors.scopes')} />
                )}
                <Tabs
                    activeKey={tab}
                    onChange={(key) => setTab(key as TabKey)}
                    items={[
                        {
                            key: 'pedagogy',
                            label: (
                                <Space size={6}>
                                    <CodeOutlined />
                                    {t('monitoring.tabs.pedagogy')}
                                </Space>
                            ),
                            children: <PedagogyTab {...tabProps} active={tab === 'pedagogy'} />,
                        },
                        {
                            key: 'ai',
                            label: (
                                <Space size={6}>
                                    <RobotOutlined />
                                    {t('monitoring.tabs.ai')}
                                </Space>
                            ),
                            children: <AiTab {...tabProps} active={tab === 'ai'} />,
                        },
                        {
                            key: 'traces',
                            label: (
                                <Space size={6}>
                                    <FileSearchOutlined />
                                    {t('monitoring.tabs.traces')}
                                </Space>
                            ),
                            children: <TracesTab {...tabProps} active={tab === 'traces'} />,
                        },
                    ]}
                />
            </Space>
        </AppLayout>
    );
};

export default MonitoringStudents;
